<template>
  <div class="callt">

    <b-table
        ref="feature-table"
        responsive
        v-bind:items="calls"
        v-bind:fields="fields"
        :busy="loading"
        class="callt-table"
    >
      <template #table-busy>
        <div class="text-center text-primary my-3">
          <b-spinner class="align-middle mx-1"></b-spinner>
          <strong>{{ msg('Loading') }}...</strong>
        </div>
      </template>

      <!-- direction: arrow squircle + label (unknown kept, as before) -->
      <template #cell(direction)="data">
        <div class="callt-dir">
          <span class="callt-dir__icon" :class="`callt-dir__icon--${dirState(data.value)}`">
            <i :class="dirIcon(data.value)"/>
          </span>
          <span class="callt-dir__label">{{ dirLabel(data.value) }}</span>
        </div>
      </template>

      <!-- caller: client name resolved above the number -->
      <template #cell(from)="data">
        <div v-if="clients[data.value] != null" class="callt-client">
          <button type="button" class="callt-client__name" @click="onClientSelect(clients[data.value])">
            {{ clients[data.value].clientFullname }}
          </button>
          <span class="callt-phone callt-client__phone tabular">{{ data.value }}</span>
        </div>
        <span v-else class="callt-phone tabular">{{ data.value }}</span>
      </template>

      <!-- receiver -->
      <template #cell(to)="data">
        <span class="callt-phone tabular">{{ data.value }}</span>
      </template>

      <!-- status pill -->
      <template #cell(status)="data">
        <span class="callt-pill" :class="`callt-pill--${statusTone(data.value)}`">
          <i :class="statusIcon(data.value)" class="mr-25"/>{{ msg(data.value) }}
        </span>
      </template>

      <!-- start time + time ago -->
      <template #cell(startTime)="data">
        <div class="callt-dates" v-if="data.value != null">
          <span class="callt-dates__main tabular">{{ $moment(data.value).format('DD.MM.YY HH:mm') }}</span>
          <span class="callt-dates__sub">{{ timeAgo(data.value) }}</span>
        </div>
        <span v-else class="callt-muted">&mdash;</span>
      </template>

      <!-- end time -->
      <template #cell(endTime)="data">
        <span v-if="data.value != null" class="callt-dates__main tabular">{{ $moment(data.value).format('DD.MM.YY HH:mm') }}</span>
        <span v-else class="callt-muted">&mdash;</span>
      </template>

      <!-- duration -->
      <template #cell(duration)="data">
        <span class="callt-duration tabular">
          <i class="fas fa-stopwatch mr-25"/>{{ formatDuration(data.value) }}
        </span>
      </template>

      <!-- price -->
      <template #cell(price)="data">
        <span v-if="data.value != null" class="callt-price tabular">
          {{ convertToEURO(data.value * (-1)) }}
        </span>
        <span v-else class="callt-muted">&mdash;</span>
      </template>

    </b-table>

    <!-- empty state -->
    <div v-if="!loading && calls.length === 0" class="callt-empty">
      <i class="fas fa-phone-volume"/>
      <p>{{ msg('nodatafound') }}</p>
    </div>

    <!-- cursor pagination (Twilio next/previous page urls) -->
    <div class="callt-foot">
      <button
          type="button"
          class="callt-page-btn"
          :disabled="page.previousPageUrl == null || loading"
          @click="goToPreviousPage"
      >
        <i class="fas fa-chevron-left mr-50"/>{{ msg('Previous') }}
      </button>

      <button
          type="button"
          class="callt-page-btn"
          :disabled="page.nextPageUrl == null || loading"
          @click="goToNextPage"
      >
        {{ msg('Next') }}<i class="fas fa-chevron-right ml-50"/>
      </button>

    </div>

  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";

export default {
  name: "CallsTable",
  components: {},
  props: ["filter"],
  watch: {
    filter: {
      deep: true,
      handler() {
        this.refresh();
      },
      immediate: true
    },
  },
  data: () => ({
    loading: true,
    clients: {},
    calls: [],
    dialogs: {
      client: false,
    },
    selected: null,

    page: {
      previousPageUrl: null,
      nextPageUrl: null,
    },
  }),
  computed: {
    ...mapGetters('data', ['getAllBranches']),
    allBranches() {
      return Object.values(this.getAllBranches)
    },
    fields() {
      let $this = this
      return [
        {
          key: 'direction',
          label: $this.msg('direction'),
          sortable: true,
          thClass: 'callt-th',
          tdClass: 'callt-td'
        },
        {
          key: 'from',
          label: $this.msg('caller'),
          sortable: true,
          thClass: 'callt-th',
          tdClass: 'callt-td'
        },
        {
          key: 'to',
          label: $this.msg('receiver'),
          sortable: true,
          thClass: 'callt-th',
          tdClass: 'callt-td'
        },
        {
          key: 'status',
          label: $this.msg('status'),
          sortable: true,
          thClass: 'callt-th',
          tdClass: 'callt-td'
        },
        {
          key: 'startTime',
          label: $this.msg('start'),
          sortable: true,
          thClass: 'callt-th',
          tdClass: 'callt-td'
        },
        {
          key: 'endTime',
          label: $this.msg('end'),
          sortable: true,
          thClass: 'callt-th',
          tdClass: 'callt-td'
        },
        {
          key: 'duration',
          label: $this.msg('duration'),
          sortable: true,
          thClass: 'callt-th',
          tdClass: 'callt-td'
        },
        {
          key: 'price',
          label: $this.msg('price'),
          sortable: true,
          thClass: 'callt-th text-right',
          tdClass: 'callt-td text-right'
        }
      ]
    },
  },
  methods: {
    ...mapActions('reception', ['fetchAllCalls']),

    goToNextPage() {
      this.filter.pageUrl = this.page.nextPageUrl;
      this.refresh();
    },
    goToPreviousPage() {
      this.filter.pageUrl = this.page.previousPageUrl;
      this.refresh();
    },

    dirState(direction) {
      const value = String(direction || '').toLowerCase();
      if (value.indexOf('inbound') === 0) {
        return 'in';
      }
      if (value.indexOf('outbound') === 0) {
        return 'out';
      }
      return 'unknown';
    },
    dirIcon(direction) {
      switch (this.dirState(direction)) {
        case 'in':
          return 'fas fa-arrow-down'
        case 'out':
          return 'fas fa-arrow-up'
        default:
          return 'fas fa-question'
      }
    },
    dirLabel(direction) {
      switch (this.dirState(direction)) {
        case 'in':
          return this.msg('inbound')
        case 'out':
          return this.msg('outbound')
        default:
          return this.msg('Unknown')
      }
    },
    statusTone(status) {
      switch (status) {
        case 'completed':
          return 'ok'
        case 'in-progress':
          return 'live'
        case 'no-answer':
        case 'busy':
          return 'warn'
        case 'failed':
        case 'canceled':
          return 'fail'
        default: // queued, ringing, ...
          return 'queue'
      }
    },
    statusIcon(status) {
      switch (status) {
        case 'completed':
          return 'fas fa-check'
        case 'in-progress':
          return 'fas fa-phone-volume'
        case 'ringing':
          return 'fas fa-bell'
        case 'no-answer':
          return 'fas fa-phone-slash'
        case 'busy':
          return 'fas fa-user-clock'
        case 'failed':
          return 'fas fa-times'
        case 'canceled':
          return 'fas fa-ban'
        default: // queued, ...
          return 'far fa-clock'
      }
    },
    formatDuration(seconds) {
      const total = parseInt(seconds, 10);
      if (isNaN(total) || total < 0) return '-';

      const minutes = Math.floor(total / 60);
      const secs = total % 60;

      if (minutes > 0) {
        return `${minutes}:${String(secs).padStart(2, '0')} min`;
      } else {
        return `${secs} s`;
      }
    },
    timeAgo(date) {
      let momentValue = this.$moment(date);
      if (!momentValue.isValid()) {
        return '';
      }
      return momentValue.fromNow();
    },
    refresh() {
      let $this = this;
      this.loading = true;
      this.$emit('busy', true);
      this.fetchData().finally(() => {
        $this.loading = false;
        $this.$emit('busy', false);
      });
    },

    onClientSelect(client) {
      this.selected = client.id
      this.dialogs.client = true
      this.$root.$emit('bv::hide::popover')
      this.$root.$emit('crm::client::select', client.id)

    },
    convertToEURO(someNumber) {
      return new Intl.NumberFormat("de-AT", {
        style: "currency",
        currency: "EUR",
      }).format(someNumber);
    },
    fetchData() {
      let $this = this;

      return this.fetchAllCalls({
        filter: this.filter
      }).then(data => {
        $this.calls = data.calls || [];

        // Store the cursor URLs for Next/Previous
        $this.page.nextPageUrl = data.nextPageUrl || null;
        $this.page.previousPageUrl = data.previousPageUrl || null;

        // Convert clients to map by phone number
        $this.clients = (data.clients || []).toObject("phone");
        $this.$emit('count', $this.calls.length);
      });
    }

  }
}
</script>


<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

.callt-table { font-size: .85rem; }
.callt-table :deep(.callt-th) {
  font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em;
  color: #94a3b8; background: rgba(15, 23, 42, .02); border-bottom: 1px solid rgba(15, 23, 42, .07) !important;
}
.callt-table :deep(.callt-td) { vertical-align: middle; padding: 10px 12px; border-top: 1px solid rgba(15, 23, 42, .05); }
.callt-table :deep(tbody tr:hover) { background: rgba(115, 103, 240, .04); }

/* direction */
.callt-dir { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.callt-dir__icon {
  width: 26px; height: 26px; border-radius: 8px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; font-size: .68rem;
  &--in { background: rgba(40, 199, 111, .13); color: #1f9d57; }
  &--out { background: rgba(115, 103, 240, .12); color: #7367f0; }
  &--unknown { background: rgba(100, 116, 139, .12); color: #64748b; }
}
.callt-dir__label {
  font-size: .64rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #94a3b8;
}

/* phones */
.callt-phone { white-space: nowrap; color: #475569; font-size: .8rem; }

/* caller (client resolved) */
.callt-client { display: flex; flex-direction: column; white-space: nowrap; }
.callt-client__name {
  border: none; background: none; padding: 0; cursor: pointer; text-align: inherit;
  font-weight: 700; color: #7367f0; font-size: .82rem; text-transform: capitalize;
  &:hover { text-decoration: underline; }
  &:focus { outline: none; }
}
.callt-client__phone { font-size: .68rem; color: #94a3b8; margin-top: 1px; }

/* status pills */
.callt-pill {
  display: inline-flex; align-items: center;
  font-size: .66rem; font-weight: 800; letter-spacing: .03em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 999px; white-space: nowrap;
  &--ok { background: rgba(40, 199, 111, .13); color: #1f9d57; }
  &--live { background: rgba(0, 207, 232, .12); color: #0090a5; }
  &--warn { background: rgba(255, 159, 67, .15); color: #b26a12; }
  &--fail { background: rgba(234, 84, 85, .13); color: #c62a2b; }
  &--queue { background: rgba(100, 116, 139, .12); color: #64748b; }
}

/* dates */
.callt-dates { display: flex; flex-direction: column; white-space: nowrap; }
.callt-dates__main { font-weight: 700; color: #1f2a44; font-size: .8rem; white-space: nowrap; }
.callt-dates__sub { font-size: .66rem; color: #94a3b8; margin-top: 2px; }

/* duration & price */
.callt-duration {
  display: inline-flex; align-items: center; white-space: nowrap;
  font-size: .78rem; font-weight: 700; color: #475569;
  i { color: #94a3b8; font-size: .68rem; }
}
.callt-price { font-weight: 800; color: #1f2a44; white-space: nowrap; }
.callt-muted { color: #cbd5e1; }

/* empty + footer */
.callt-empty { text-align: center; padding: 40px 0 26px; color: #94a3b8; i { font-size: 2rem; opacity: .4; } p { margin-top: 8px; font-size: .85rem; } }
.callt-foot {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding-top: 12px; border-top: 1px solid rgba(15, 23, 42, .06);
}
.callt-page-btn {
  display: inline-flex; align-items: center;
  border: 1px solid rgba(15, 23, 42, .1); border-radius: 999px; background: transparent;
  color: #64748b; font-size: .76rem; font-weight: 700; cursor: pointer;
  padding: 6px 16px; transition: all .15s;
  &:hover:not(:disabled) { border-color: #7367f0; color: #7367f0; background: rgba(115, 103, 240, .08); }
  &:disabled { opacity: .45; cursor: default; }
  &:focus { outline: none; }
}
</style>

<style lang="scss">
.dark-layout {
  .callt-table .callt-th { background: rgba(255, 255, 255, .03); color: #8a92a6; border-color: rgba(255, 255, 255, .08) !important; }
  .callt-table .callt-td { border-color: rgba(255, 255, 255, .06); }
  .callt-table tbody tr:hover { background: rgba(115, 103, 240, .1); }
  .callt-phone { color: #b4b7bd; }
  .callt-dates__main, .callt-price { color: #e9ecf2; }
  .callt-duration { color: #b4b7bd; }
  .callt-muted { color: #4b5261; }
  .callt-foot { border-color: rgba(255, 255, 255, .08); }
  .callt-page-btn { border-color: rgba(255, 255, 255, .16); color: #94a3b8; }
}
</style>
